import {
	Catharsis,
	CatharsisArgs,
	getCatharsis,
	getInvalidationHandler,
	MegaStoreWithSubscriptions,
} from '@wix/thunderbolt-catharsis'
import {
	editorCssFeatures,
	componentCssFeaturesProvider,
	IsExperimentOpen,
} from '@wix/thunderbolt-catharsis-extensions'
import schemas from '@wix/document-services-json-schemas/dist/refData.json'
import { cursorShapeIdsNode, shapeIdsDataNodes } from '@wix/thunderbolt-fetch-shapes'
import { vectorImage } from '@wix/thunderbolt-commons'
import { FetchFn } from '@wix/thunderbolt-becky-types'
import { ServiceTopology } from '@wix/document-services-types'

type EditorCatharsis = {
	catharsis: Catharsis
	catharsisMegaStore: MegaStoreWithSubscriptions | null
	usedDataMaps: Set<string>
}

type EditorCatharsisArgs = {
	isExperimentOpen: IsExperimentOpen
	fetchFn: FetchFn
	serviceTopology: ServiceTopology
}

export const getEditorCatharsis = ({
	isExperimentOpen,
	fetchFn,
	serviceTopology,
}: EditorCatharsisArgs): EditorCatharsis => {
	const { componentNodes, usedDataMaps } = componentCssFeaturesProvider(editorCssFeatures, isExperimentOpen)

	const { buildSvgUrl } = vectorImage.buildSvgUrlFactory()

	const fetchSvg = (shapeId: string) =>
		fetchFn(buildSvgUrl(serviceTopology.mediaRootUrl, shapeId))
			.then((res) => (res.ok ? res.text() : null))
			.catch(() => null)
			.then((res) => res || `<svg data-svg-id="fallback-${shapeId}" />`)

	const catharsisMegaStore = new MegaStoreWithSubscriptions()
	const cssInvalidation = getInvalidationHandler(catharsisMegaStore)
	const shapeIds = new Set<string>()
	const onShapeInvalidation: CatharsisArgs['onInvalidation'] = (path, get) => {
		const [, aspect] = path
		if (aspect === 'cursorShapeIds') {
			const cursorShapeIds: Array<string> = get(path)

			if (!cursorShapeIds) {
				return
			}

			for (const shapeId of cursorShapeIds) {
				if (!shapeIds.has(shapeId)) {
					shapeIds.add(shapeId)
					fetchSvg(shapeId).then((svgString) => {
						catharsis.transaction((ops) => ops.updateSvgString(shapeId, svgString))
					})
				}
			}
		}
	}
	const structureStore = catharsisMegaStore.getChildStore('structure')
	const catharsis = getCatharsis({
		onInvalidation: (path, get) => {
			cssInvalidation(path, get)
			onShapeInvalidation(path, get)
		},
		extensions: {
			componentNodes: { ...componentNodes, cursorShapeIds: cursorShapeIdsNode },
			dataNodes: shapeIdsDataNodes,
			schemas,
		},
		structureStore,
	})

	return { catharsis, catharsisMegaStore, usedDataMaps }
}
